import { DollarSign } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";
import { StatCard, SectionHeader, fmt$ } from "./ReportsShared";

const VolumeTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="px-3 py-2 rounded-lg text-xs shadow-lg" style={{ background: "var(--t-card)", border: "1px solid var(--t-border)" }}>
      <p className="font-medium mb-1" style={{ color: "var(--t-text)" }}>{label}</p>
      <p style={{ color: "#059669" }}>Volume: {fmt$(payload[0].value)}</p>
    </div>
  );
};

const WarrantyTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="px-3 py-2 rounded-lg text-xs shadow-lg" style={{ background: "var(--t-card)", border: "1px solid var(--t-border)" }}>
      <p className="font-medium mb-1" style={{ color: "var(--t-text)" }}>{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }}>{p.name}: ${typeof p.value === "number" ? p.value.toLocaleString() : p.value}</p>
      ))}
    </div>
  );
};

export const FinancialSection = ({ financial }) => (
  <section data-testid="section-financial" id="section-financial">
    <SectionHeader icon={DollarSign} title="Financial Summary" subtitle="Volume, pricing, and earnest money" />
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      <StatCard icon={DollarSign} label="Active Volume" value={fmt$(financial.active_volume)} color="#2563eb" />
      <StatCard icon={DollarSign} label="Closed Volume" value={fmt$(financial.closed_volume)} color="#059669" />
      <StatCard icon={DollarSign} label="Avg Purchase Price" value={fmt$(financial.avg_purchase_price)} color="#7c3aed" />
      <StatCard icon={DollarSign} label="Total Earnest Money" value={fmt$(financial.total_earnest_money)} color="#D97706" />
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="card-surface" data-testid="volume-monthly-chart">
        <CardHeader className="py-3 px-5">
          <CardTitle className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>Closed Volume by Month</CardTitle>
        </CardHeader>
        <CardContent className="px-2 pb-4">
          {financial.volume_by_month.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={financial.volume_by_month}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--t-text-muted)" }} />
                <YAxis tick={{ fontSize: 11, fill: "var(--t-text-muted)" }} tickFormatter={(v) => fmt$(v)} />
                <Tooltip content={<VolumeTooltip />} />
                <Area type="monotone" dataKey="volume" name="Volume" stroke="#059669" fill="#059669" fillOpacity={0.15} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center py-10 text-sm" style={{ color: "var(--t-text-muted)" }}>No closed volume data yet</p>
          )}
        </CardContent>
      </Card>
      <Card className="card-surface" data-testid="warranty-card">
        <CardHeader className="py-3 px-5">
          <CardTitle className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>Home Warranty Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center pb-4 h-[220px]">
          {(financial.home_warranty.buyer_paid > 0 || financial.home_warranty.seller_paid > 0) ? (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie
                    data={[
                      { name: "Buyer Paid", value: financial.home_warranty.buyer_paid, color: "#2563eb" },
                      { name: "Seller Paid", value: financial.home_warranty.seller_paid, color: "#D97706" },
                    ].filter((d) => d.value > 0)}
                    cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value"
                  >
                    <Cell fill="#2563eb" />
                    <Cell fill="#D97706" />
                  </Pie>
                  <Tooltip content={<WarrantyTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex gap-6 text-xs mt-2">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#2563eb]" /> Buyer: {fmt$(financial.home_warranty.buyer_paid)}</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#D97706]" /> Seller: {fmt$(financial.home_warranty.seller_paid)}</span>
              </div>
            </>
          ) : (
            <p className="text-sm" style={{ color: "var(--t-text-muted)" }}>No home warranty data</p>
          )}
        </CardContent>
      </Card>
    </div>
  </section>
);
